import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, LogIn, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { href: "#about", label: "SPOT" },
  { href: "#gallery", label: "PHOTOS" },
  { href: "#games", label: "GAMES" },
  { href: "#instagram", label: "IG" },
  { to: "/hyena-dungeon", label: "HYENA" },
  { href: "#contact", label: "CONTACT" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between gap-4">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <span className="h-2.5 w-2.5 rounded-full bg-sky-400 shadow-[0_0_16px_rgba(56,189,248,0.9)]" />
          <span className="font-display text-white text-sm md:text-base tracking-widest uppercase group-hover:text-sky-400 transition-colors">
            TED <span className="text-sky-400">/</span> FUN SPOT
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) =>
            l.to ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono text-[11px] tracking-[0.25em] text-red-400 hover:text-red-300 transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono text-[11px] tracking-[0.25em] text-zinc-400 hover:text-sky-400 transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:2221020988"
            data-testid="header-phone"
            className="hidden md:inline-flex items-center gap-2 text-xs font-mono tabular-nums text-zinc-300 hover:text-sky-400 transition-colors px-3 py-2 border border-zinc-800 hover:border-sky-400/50"
          >
            <Phone className="w-3.5 h-3.5" />
            22210 20988
          </a>

          {user && user !== false ? (
            <div className="flex items-center gap-1">
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="header-admin"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest border border-sky-800 text-sky-400 px-3 py-2 hover:border-sky-400"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Admin</span>
                </Link>
              )}
              <button
                onClick={logout}
                data-testid="header-logout"
                className="inline-flex items-center justify-center border border-zinc-800 text-zinc-400 hover:text-sky-400 hover:border-sky-400 p-2"
                title="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login"
                className="inline-flex items-center gap-1.5 border border-zinc-800 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:border-sky-400 hover:text-sky-400"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Login</span>
              </Link>
              <a
                href="https://www.instagram.com/ted.cafe.chk/"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="header-instagram"
                className="hidden sm:inline-flex items-center gap-2 bg-sky-400 text-black text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-sky-300 transition-colors"
              >
                <Instagram className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Follow</span>
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
